import { networkManager } from './NetworkManager';

interface ApiResponse<T> {
  status: boolean;
  data: T;
}

export type AuthMode = 'otp' | 'test';

/** Tokens issued after OTP verification (camelCase, matching the API envelope). */
export interface AuthTokens {
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
  tokenType: string;
  profile?: { yumId: string; email: string };
}

export type Role = 'admin' | 'legal' | 'fbc' | 'finance' | 'cdto' | 'coo' | 'gtm';
/** BMUs are brand-scoped, so a BMU grant is brand-qualified: each entry is a (brand, BMU) pair. */
export interface BmuScopeEntry {
  brand: string;
  bmu: string;
}
export type Scope =
  | { type: 'global' }
  | { type: 'brand'; brands: string[] }
  | { type: 'bmu'; bmus: BmuScopeEntry[] };

export interface CurrentUser {
  sub: string;
  email?: string;
  username?: string;
  yumId?: string;
  name?: string;
  title?: string;
  role?: Role;
  scope?: Scope;
  franchisee?: { id: string; name: string; brand: string; bmu: string; country: string };
  /** Set during an admin emulation session: the acting admin's YUM_ID. */
  emulatedBy?: string;
}

export interface EmulateResult {
  accessToken: string;
  refreshToken?: string;
  target: { yumId: string; name?: string };
}

/** Admin: mint read-only emulation tokens for a YUM_ID (global-admin only, audited). */
export async function emulateUser(yumId: string): Promise<EmulateResult> {
  const response = await networkManager.post<ApiResponse<EmulateResult>>('/api/admin/emulate', { yumId });
  return response.data;
}

/** True for an internal user with an access grant (sees the admin area). */
export function isAdminUser(user: CurrentUser | null): boolean {
  return !!user?.role;
}

/** Request a one-time sign-in code for a Yum ID (emailed to the user via SES). */
export async function sendOtp(yumId: string): Promise<void> {
  await networkManager.post<ApiResponse<{ sent: true }>>('/api/auth/otp/send', { yumId });
}

/** Verify a one-time code; returns access + refresh tokens. */
export async function verifyOtp(yumId: string, otp: string): Promise<AuthTokens> {
  const response = await networkManager.post<ApiResponse<AuthTokens>>('/api/auth/otp/verify', {
    yumId,
    otp,
  });
  return response.data;
}

export async function getCurrentUser(): Promise<CurrentUser> {
  const response = await networkManager.get<ApiResponse<CurrentUser>>('/api/auth/me');
  return response.data;
}

export async function getAuthMode(): Promise<AuthMode> {
  const response = await networkManager.get<ApiResponse<{ authMode: AuthMode }>>('/api/auth/mode');
  return response.data.authMode;
}

/** Non-prod only: mint a test token for a persona (the human counterpart to the E2E loginAs helper). */
export async function devLogin(yumId: string): Promise<string> {
  const response = await networkManager.post<ApiResponse<{ id_token: string }>>('/api/auth/test-login', { yumId });
  return response.data.id_token;
}
